import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, Chip, CircularProgress,
  Grid, InputAdornment, LinearProgress, Stack, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import AddHomeWorkOutlinedIcon from '@mui/icons-material/AddHomeWorkOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SearchIcon from '@mui/icons-material/Search';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import { useHistory } from 'react-router-dom';
import ResidenceForm from './ResidenceForm';
import ResidenceMembers from './ResidenceMembers';
import RoomForm from './RoomForm';
import ResidenceOverview from './ResidenceOverview';
import HouseholdProjects from '../Households/HouseholdProjects';
import HouseholdTasks from '../Households/HouseholdTasks';
import HouseholdFiles from '../Households/HouseholdFiles';
import HouseholdNotes from '../Households/HouseholdNotes';
import { APIURL } from '../../config';

const Residences = (props) => {
  const { user } = props;
  const history = useHistory();
  const [residences, setResidences] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [roomFormOpen, setRoomFormOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const selected = useMemo(
    () => residences.find((residence) => residence.residence_id === selectedId) || null,
    [residences, selectedId]
  );

  const fetchResidences = useCallback(async (preferredId) => {
    if (!user?.comp_id) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${APIURL}/residences`, { params: { company_id: user.comp_id } });
      const list = Array.isArray(data) ? data : [];
      setResidences(list);
      setSelectedId((current) => {
        const candidate = preferredId || current;
        return list.some((item) => item.residence_id === candidate) ? candidate : list[0]?.residence_id || null;
      });
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to load residences.');
      setResidences([]);
      setSelectedId(null);
    } finally {
      setLoading(false);
    }
  }, [user?.comp_id]);

  useEffect(() => { fetchResidences(); }, [fetchResidences]);

  const fetchResidenceDetails = useCallback(async () => {
    if (!selectedId || !user?.comp_id) {
      setAssignments([]);
      setRooms([]);
      return;
    }
    try {
      const [assignmentResponse, roomResponse] = await Promise.all([
        axios.get(`${APIURL}/residences/assignments`, {
          params: { company_id: user.comp_id, residence_id: selectedId, active: true },
        }),
        axios.get(`${APIURL}/residences/rooms`, {
          params: { company_id: user.comp_id, residence_id: selectedId },
        }),
      ]);
      setAssignments(Array.isArray(assignmentResponse.data) ? assignmentResponse.data : []);
      setRooms(Array.isArray(roomResponse.data) ? roomResponse.data : []);
    } catch (requestError) {
      setAssignments([]);
      setRooms([]);
    }
  }, [selectedId, user?.comp_id]);

  useEffect(() => { fetchResidenceDetails(); }, [fetchResidenceDetails]);

  const refreshResidence = async () => {
    await Promise.all([fetchResidenceDetails(), fetchResidences(selectedId)]);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };

  const renderRooms = () => (
    <Box sx={{ mt: 2, p: 1 }}>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<MeetingRoomOutlinedIcon />} onClick={() => setRoomFormOpen(true)}>Add Room</Button>
      </Stack>
      {!rooms.length && <Alert severity="info">No rooms have been added to this residence.</Alert>}
      <Grid container spacing={2}>
        {rooms.map((room) => {
          const occupied = Number(room.current_occupancy) || 0;
          const beds = Number(room.no_of_beds) || 0;
          const available = Math.max(0, beds - occupied);
          const percent = beds ? Math.min(100, (occupied / beds) * 100) : 0;
          return (
            <Grid item xs={12} sm={6} md={4} key={room.room_id}>
              <Card elevation={0} sx={{ height: '100%', border: '1px solid #dce8f5', borderRadius: 2, overflow: 'hidden', transition: 'transform 0.15s ease, box-shadow 0.15s ease', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 18px rgba(30, 95, 145, 0.12)' } }}>
                <Box sx={{ p: 1.5, bgcolor: '#F7FBFE', borderBottom: '1px solid #e0ecfa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: '#EBF8FF', color: '#3FA7D6', display: 'grid', placeItems: 'center' }}><MeetingRoomOutlinedIcon /></Box>
                    <Box><Typography fontWeight={700}>{room.room_identifier}</Typography><Typography variant="caption" color="text.secondary">{room.room_type}</Typography></Box>
                  </Stack>
                  <Chip label={available ? `${available} open` : 'Full'} size="small" color={available ? 'success' : 'default'} />
                </Box>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}><Typography variant="body2" fontWeight={600}>Bed occupancy</Typography><Typography variant="body2">{occupied} / {beds}</Typography></Stack>
                  <LinearProgress variant="determinate" value={percent} color={available ? 'primary' : 'inherit'} sx={{ height: 8, borderRadius: 5, mb: 1.5 }} />
                  <Stack direction="row" spacing={1}>
                    <Chip label={`${beds} bed${beds === 1 ? '' : 's'}`} size="small" variant="outlined" />
                    {room.is_accessible && <Chip label="Accessible" size="small" variant="outlined" color="primary" />}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );

  const renderTabContent = () => {
    switch (tab) {
      case 0:
        return <ResidenceOverview residence={selected} assignments={assignments} rooms={rooms} onNavigateToRooms={() => setTab(1)} onNavigateToMembers={() => setTab(2)} />;
      case 1:
        return renderRooms();
      case 2:
        return <Box sx={{ mt: 2 }}><ResidenceMembers residence={selected} assignments={assignments} rooms={rooms} user={user} onChanged={refreshResidence} /></Box>;
      case 3:
        return <HouseholdProjects {...props} history={history} members={assignments} user={user} entityLabel="residence" />;
      case 4:
        return <HouseholdTasks {...props} history={history} members={assignments} user={user} entityLabel="residence" />;
      case 5:
        return <HouseholdFiles members={assignments} user={user} customerLabel="Customer" entityLabel="residence" />;
      case 6:
        return <HouseholdNotes {...props} members={assignments} user={user} customerLabel="Customer" entityLabel="residence" />;
      default:
        return null;
    }
  };

  if (loading) return <Box sx={{ height: '70vh', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;

  return (
    <div style={{ height: 'calc(100vh - 90px)', width: '100%', overflow: 'hidden' }}>
      <div className="householdsPage-mainDiv" style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
        {error && <Alert severity="error" sx={{ mx: 1, mt: 1 }}>{error}</Alert>}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px' }}>
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingRight: '15px', borderRight: '2px solid #257bcd', flexDirection: 'column', textAlign: 'right' }}>
              <span style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>Current Residence</span>
              <span style={{ fontSize: '18px', fontWeight: '600', color: selected ? '#333' : '#999', fontStyle: selected ? 'normal' : 'italic' }}>
                {selected?.residence_name || 'No residence selected'}
              </span>
            </div>
            <Autocomplete
              freeSolo
              clearOnBlur
              disableClearable
              inputValue={inputValue}
              onInputChange={(event, value) => setInputValue(value)}
              options={residences}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.residence_name || ''}
              isOptionEqualToValue={(option, value) => option.residence_id === value.residence_id}
              onChange={(event, value) => {
                if (value && typeof value === 'object') {
                  setSelectedId(value.residence_id);
                  setInputValue(value.residence_name);
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Residences" variant="outlined" size="small" style={{ width: '300px' }} InputProps={{
                  ...params.InputProps,
                  startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                }} />
              )}
              renderOption={(optionProps, residence) => <li {...optionProps} key={residence.residence_id}>{residence.residence_name}</li>}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            {selected && <Button variant="contained" color="secondary" startIcon={<EditOutlinedIcon />} onClick={() => { setEditing(selected); setFormOpen(true); }}>Edit Residence</Button>}
            <Button variant="contained" color="primary" startIcon={<AddHomeWorkOutlinedIcon />} onClick={() => setFormOpen(true)}>New Residence</Button>
          </div>
        </div>

        <div style={{ display: 'flex', height: 'calc(100vh - 200px)' }}>
          <div style={{ width: '100%' }}>
            {!selected && (
              <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '500px', marginTop: 10 }}>
                <img src="./nosearchresult.png" alt="no residences" style={{ width: '15rem', height: '10rem', margin: 10 }} />
                <h5 style={{ fontFamily: 'Lato', marginTop: 48, marginBottom: 30 }} className="text-center">No Residences Found</h5>
                <p style={{ fontFamily: 'Lato', fontSize: 12, width: '70%' }} className="text-muted text-center text-wrap">
                  Residence information including members, location, and service details will show here once you add residences.
                  <br />On the top, you can search for residences.
                </p>
              </div>
            )}
            {selected && (
              <div style={{ width: '100%', height: '100%', paddingLeft: '15px', display: 'flex', flexDirection: 'column' }}>
                <Tabs value={tab} onChange={(event, value) => setTab(value)} variant="fullWidth" scrollButtons="auto" style={{ flexShrink: 0, width: '100%' }}>
                  <Tab label="OVERVIEW" value={0} />
                  <Tab label="ROOMS" value={1} />
                  <Tab label="MEMBERS" value={2} />
                  <Tab label="PROJECTS" value={3} />
                  <Tab label="TASKS" value={4} />
                  <Tab label="FILES" value={5} />
                  <Tab label="NOTES" value={6} />
                </Tabs>
                <div className="details_body" style={{ overflow: 'auto', flex: 1, minHeight: 0, paddingRight: '12px' }}>
                  {renderTabContent()}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ResidenceForm
        open={formOpen}
        onClose={closeForm}
        onSave={async () => { const id = editing?.residence_id; closeForm(); await fetchResidences(id); }}
        residence={editing}
        user={user}
      />
      {selected && <RoomForm open={roomFormOpen} onClose={() => setRoomFormOpen(false)} onSave={refreshResidence} residence={selected} user={user} />}
    </div>
  );
};

export default Residences;
